import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
  Table,
  Button,
  Modal,
  notification,
  Spin,
  Empty
} from "antd";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineChevronLeft
} from 'react-icons/hi';
import "./ManageServiceMaster.css";

const ManageServiceMaster = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const doctorId = Cookies.get("Doctor_id") || Cookies.get("doctor_id") || localStorage.getItem("Doctor_id") || localStorage.getItem("doctor_id");
      const response = await axiosInstance.post("doctor/getDoctorService", {
        doctor_id: doctorId
      });
      if (response.data.status === 1) {
        setServices(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch services", err);
      notification.error({ message: "Failed to load services" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this service?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        try {
          const response = await axiosInstance.post("doctor/delete_mas_doctor_service_type", { id });
          if (response.data.status === 1 || response.data.ErrorCode === "9999" || response.data.data?.[0]?.ErrorCode === "9999") {
            notification.success({ message: "Service deleted successfully" });
            await fetchServices();
          } else {
            notification.error({ message: response.data.msg || "Delete failed" });
          }
        } catch (err) {
          console.error("Delete error", err);
          notification.error({ message: "Failed to delete service" });
        }
      }
    });
  };

  const currency = "₹";
  const defaultDoctorName = Cookies.get("Doctor_name") || Cookies.get("doctor_Name") || "Doctor 20";

  const columns = [
    {
      title: 'Image',
      dataIndex: 'file_path',
      key: 'file_path',
      width: 70,
      render: (img) => {
        const isValidUrl = img && typeof img === 'string' && img.startsWith('http') && !img.includes('image.png');
        return isValidUrl ? (
          <img src={img} alt="Service" className="w-7 h-7 rounded-md object-cover border border-gray-200 shadow-2xs" />
        ) : (
          <div className="w-7 h-7 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-400 tracking-wider">
            NONE
          </div>
        );
      }
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 140,
      render: (text, record) => (
        <span className="font-bold text-gray-800 text-xs">
          {text || record.doctor_name || defaultDoctorName}
        </span>
      ),
    },
    {
      title: 'Service Name',
      dataIndex: 'service',
      key: 'service',
      width: 170,
      render: (text) => <span className="font-bold text-gray-800 text-xs">{text}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => <span className="text-gray-500 font-normal text-xs">{text || '-'}</span>
    },
    {
      title: 'Duration',
      dataIndex: 'slot_duration',
      key: 'slot_duration',
      width: 90,
      render: (text) => {
        const mins = text ? (text.includes(':') ? parseInt(text.split(':')[1]) : text) : '15';
        return (
          <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-bold px-2 py-0.5 rounded">
            {mins} min
          </span>
        );
      }
    },
    {
      title: 'Fee',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (text) => <span className="font-bold text-[#009688] text-xs">{currency} {Number(text || 0).toFixed(2)}</span>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 85,
      render: (status) => (
        <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded border ${
          status == 1
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-gray-100 text-gray-500 border-gray-200"
        }`}>
          {status == 1 ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 75,
      align: 'right',
      render: (_, record) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => history.push(`/dashboard/services/editservice/${record.id}`)}
            className="p-1 hover:bg-blue-50 rounded text-blue-600 transition-colors border-none bg-transparent cursor-pointer"
            title="Edit Service"
          >
            <HiOutlinePencil size={15} />
          </button>
          <button
            onClick={() => handleDelete(record.id)}
            className="p-1 hover:bg-rose-50 rounded text-rose-600 transition-colors border-none bg-transparent cursor-pointer"
            title="Delete Service"
          >
            <HiOutlineTrash size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-2.5 animate-in fade-in duration-300 w-full text-gray-800">
      {/* Services Table Card - Compact Full Width */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-2xs overflow-hidden w-full">
        {/* Sleek Header Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white gap-3 sm:gap-0">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => history.push('/dashboard')}
              className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center shrink-0"
              title="Back"
            >
              <HiOutlineChevronLeft size={18} />
            </button>
            <div>
              <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">Manage Services</h2>
              <p className="text-gray-400 text-[11px] m-0 font-medium">Configure your clinic's medical service offerings</p>
            </div>
          </div>
          <button
            onClick={() => history.push('/dashboard/services/addservice')}
            className="flex items-center gap-1.5 bg-[#009688] hover:bg-[#00897b] text-white px-3.5 py-1.5 rounded-lg font-bold text-xs transition-all shadow-2xs cursor-pointer border-none w-full sm:w-auto justify-center sm:justify-start"
          >
            <HiOutlinePlus size={15} />
            <span>Add New Service</span>
          </button>
        </div>

        {loading ? (
          <div className="py-10 flex flex-col items-center justify-center gap-2">
            <Spin size="small" />
            <p className="text-gray-400 font-medium text-xs m-0">Loading services...</p>
          </div>
        ) : services.length > 0 ? (
          <Table
            columns={columns}
            dataSource={services}
            rowKey="id"
            size="small"
            scroll={{ x: 800 }}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            className="custom-table"
          />
        ) : (
          <div className="py-12 text-center bg-gray-50/20">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="max-w-xs mx-auto">
                  <p className="text-gray-800 font-bold text-xs m-0">No Services Defined</p>
                  <p className="text-gray-400 text-[11px] mt-0.5 mb-3">Add your first clinic service offering to allow patient bookings.</p>
                  <Button
                    type="primary"
                    className="bg-[#009688] hover:bg-[#00897b] border-none rounded-lg h-8 px-4 font-bold text-xs"
                    onClick={() => history.push('/dashboard/services/addservice')}
                  >
                    Add New Service
                  </Button>
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageServiceMaster;
